import React from 'react';
import PropTypes from 'prop-types';
import Raven from 'raven-js';
import { connect } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import uuid from 'uuid';

import Align from '@marvelapp/marvel-ui/align';
import Button from '@marvelapp/marvel-ui/button';
import MarvelLink from '@marvelapp/marvel-ui/link';
import MaxWidth from '@marvelapp/marvel-ui/max-width';
import MessageBlock from '@marvelapp/marvel-ui/message-block';
import Paragraph from '@marvelapp/marvel-ui/paragraph';
import Spacing from '@marvelapp/marvel-ui/spacing';

import { getEnterpriseFormContent } from 'marketing-website/configs/hubspot';
import UnderlinedInputField from 'marketing-website/utils/forms/UnderlinedInputField';
import UnderlinedSelectField from 'marketing-website/utils/forms/UnderlinedSelectField';
import { readCookie, getUtmfromCookies } from 'marketing-website/utils/params';
import { splitNames } from 'marketing-website/utils/forms';
import {
  isRequired,
  isEmailValid,
  shouldBeSelected,
} from 'marketing-website/utils/forms/validation';
import { fetchApi } from 'marketing-website/utils/fetch';
import { sendGaPageView } from 'marketing-website/utils/analytics';
import { API_ENTERPRISE } from 'marketing-website/constants/api';
import { getUsername, getEmail, getCompanyName } from '../../../selectors';

import PhoneInput from './PhoneInput';

const fields = [
  {
    fieldOptions: {
      name: 'name',
      size: 'large',
      placeholder: 'Please enter your full name',
      label: 'Full Name',
    },
    validate: isRequired,
  },
  {
    fieldOptions: {
      name: 'email',
      size: 'large',
      placeholder: 'Please enter your company email address',
      label: 'Work Email Address',
      type: 'email',
    },
    validate: [isRequired, isEmailValid],
  },
  {
    fieldOptions: {
      name: 'phoneNumber',
      size: 'large',
      placeholder: 'Please enter your phone number',
      label: 'Phone Number',
      type: 'tel',
    },
    component: props => (
      <UnderlinedInputField {...props}>
        <PhoneInput {...props} />
      </UnderlinedInputField>
    ),
  },
  {
    fieldOptions: {
      name: 'companyName',
      size: 'large',
      placeholder: 'Please enter a company name',
      label: 'Company Name',
    },
    validate: [isRequired],
  },
  {
    component: UnderlinedSelectField,
    fieldOptions: {
      name: 'users',
      size: 'l',
      label: 'Users required',
      options: [
        {
          value: 'Select users required',
          label: 'Select users required',
          disabled: true,
          selected: true,
        },
        {
          value: '7-9',
          label: '7-9',
        },
        {
          value: '10-49',
          label: '10-49',
        },
        {
          value: '50-99',
          label: '50-99',
        },
        {
          value: '100+',
          label: '100+',
        },
      ],
    },
    validate: [shouldBeSelected],
  },
];
class EnterpriseForm extends React.Component {
  state = {
    formState: 'initial',
  };

  submitForm = ({
    name,
    email,
    phoneNumber,
    companyName,
    users: usersRequired,
  }) => {
    if (window.drift) {
      try {
        window.drift.unload();
      } catch (e) {
        console.error('There was a problem unloading Drift');
      }
    }
    // Sometimes the form Id might change from the url parameter
    const hubspotFormId = this.props.hubspotId;
    const names = splitNames(name);
    const firstName = names.firstName;
    const lastName = names.lastName;
    const utmParams = getUtmfromCookies();
    const gclid = readCookie('gclid'); // The GCLID cookie is a way to track users who
    // clicked through to the site from an ad
    const fbclid = readCookie('fbclid'); // The FBCLID cookie is a way to track users who click through from FB ad
    const landingPage = readCookie('landingPage');
    const formName = getEnterpriseFormContent(hubspotFormId).formName;
    const formId = uuid.v4();

    let pageUrl = '';
    if (typeof window !== `undefined`) {
      pageUrl = window.location.href;
    }

    const postData = {
      name,
      hubspotFormId,
      formName,
      first_name: firstName,
      last_name: lastName,
      email,
      companyName,
      phoneNumber,
      internationalNumber: phoneNumber,
      usersRequired,
      referrer: document.referrer,
      gclid,
      fbclid,
      landingPage,
      ...utmParams,
      page_url: pageUrl,
      hutk: readCookie('hubspotutk'),
      form_uuid: formId,
    };
    fetchApi('POST', API_ENTERPRISE, postData)
      .then(() => {
        this.setState({
          formState: 'success',
        });
      })
      .catch(() => {
        this.setState({
          formState: 'error',
        });
      });

    if (typeof window !== `undefined` && window.dataLayer) {
      let eventName = 'form-submitted-enterprise-page';
      if (window.location.pathname === '/enterprise/request-quote/') {
        eventName = 'form-submitted-enterprise-pricing-page';
      }
      window.dataLayer.push({
        event: eventName,
      });
    }

    try {
      if (typeof window !== `undefined` && window.fbq) {
        window.fbq('track', 'Lead');
      }
    } catch (ex) {
      console.error('Issue tracking FB lead', ex);
    }

    try {
      sendGaPageView('/enterprise-lead/');
    } catch (ex) {
      Raven.captureException(ex);
      console.error('Issue tracking Enterprise lead', ex);
    }
  };

  render() {
    const {
      handleSubmit,
      ctaCopy,
      btnColor,
      headerTitle,
      headerText,
    } = this.props;
    const { formState } = this.state;
    const successState = formState === 'success';

    return (
      <div>
        {headerTitle}
        {headerText}

        <form
          onSubmit={handleSubmit(this.submitForm)}
          className="display-block"
          noValidate
          method="post"
          action={API_ENTERPRISE}
        >
          {formState === 'error' && (
            <div className="marginBottom-m">
              <MessageBlock type="error">
                There was an error submitting this form. Please try again or{' '}
                <MarvelLink
                  url="https://help.marvelapp.com/"
                  type="opaqueReversed"
                  hasUnderline
                  target="_blank"
                >
                  contact support.
                </MarvelLink>
              </MessageBlock>
            </div>
          )}
          {fields.map(field => (
            <Spacing
              key={field.fieldOptions.name}
              type="margin"
              size="xs"
              position="bottom"
            >
              <Field
                {...field.fieldOptions}
                component={field.component || UnderlinedInputField}
                fullWidth
                validate={field.validate}
                popoverPosition="top"
                popoverWidth={400}
              />
            </Spacing>
          ))}
          <div className="marginTop-m">
            <Button
              type="submit"
              shape="rounded"
              size="large"
              background={successState ? 'green' : btnColor}
              hover={successState ? 'green' : btnColor}
              iconColor="white"
              hasSucceeded={successState}
              isStateful
            >
              {ctaCopy}
            </Button>
          </div>
          {successState && (
            <Align type="center">
              <MaxWidth value={300}>
                <Spacing type="margin" size="m" position="top">
                  <Spacing type="padding" size="m" position="leftRight">
                    <Paragraph size="m" color="silver">
                      Thank you! Someone from our team will be in touch shortly.
                    </Paragraph>
                  </Spacing>
                </Spacing>
              </MaxWidth>
            </Align>
          )}
        </form>
      </div>
    );
  }
}

EnterpriseForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  ctaCopy: PropTypes.string,
  btnColor: PropTypes.string,
  headerTitle: PropTypes.string,
  headerText: PropTypes.string,
  hubspotId: PropTypes.string.isRequired,
};

EnterpriseForm.defaultProps = {
  btnColor: 'blue',
  ctaCopy: 'Request a trial',
  userName: null,
  email: null,
  phoneNumber: null,
  companyName: null,
};

function mapStateToProps(state) {
  const returnValues = {
    initialValues: {
      email: getEmail(state),
      name: getUsername(state),
      companyName: getCompanyName(state),
    }, // pull initial values from account reducer
  };
  return returnValues;
}

// eslint-disable-next-line
EnterpriseForm = reduxForm({
  form: 'enterpriseForm',
  touchOnBlur: false,
})(EnterpriseForm);

export default connect(mapStateToProps)(EnterpriseForm);



// WEBPACK FOOTER //
// ./src/components/modules/EnterpriseForm/index.js